import type { IntegrationProvider } from '@services/integrationService';

/**
 * REV-492: the provider catalog.
 *
 * Integrations used to be a hardcoded GitHub form. This registry is what makes
 * the page answer "what CAN I connect?" rather than only "what have I
 * connected?" — a user could not discover that Jira is coming without reading
 * the source.
 *
 * `available: false` providers are shown deliberately rather than hidden,
 * because a user cannot plan around a capability they cannot see.
 *
 * REV-695: the REASON recorded here was out of date, which is worse than no
 * reason. It said the API's service rejects non-github creates. That was true
 * when REV-492 was written and is no longer: REV-74 landed, and
 * integrationConnectionService now has SUPPORTED_PROVIDERS = ['github', 'jira']
 * with both route validators accepting jira and shape-checking its config
 * (baseUrl, email, projectKey, issueType).
 *
 * The blocker moved to THIS side. The connect dialog in IntegrationsPage is
 * still entirely GitHub-shaped - its title is "Connect GitHub Issues" and its
 * three fields are repository owner, repository name and access token. Jira
 * needs a different set entirely, so flipping this flag today would open a
 * GitHub form for Jira and produce a connection that cannot work.
 *
 * So `available: false` is still correct, for a different reason. What is left
 * is a provider-aware connect form; the API is ready and waiting.
 */
export interface ProviderDescriptor {
  /** Matches the API's provider value. Also used as the catalog route segment. */
  id: IntegrationProvider | 'jira';
  label: string;
  /** One line on what connecting it buys you. */
  blurb: string;
  /** False until this UI can collect everything the provider's connection needs. */
  available: boolean;
  /**
   * Shown next to the "not yet available" chip. REV-695: without it the UI
   * states a fact and withholds the reason, which is what prompted the ticket.
   */
  unavailableReason?: string;
}

export const PROVIDERS: ProviderDescriptor[] = [
  {
    id: 'github',
    label: 'GitHub Issues',
    blurb: 'Push a finding to a repository as a tracked issue.',
    available: true,
  },
  {
    id: 'jira',
    label: 'Jira',
    blurb: 'Push a finding to a Jira project as an issue.',
    available: false,
    // REV-695: the walkthrough asked "it says Jira is not available, and I
    // don't know why". The chip said only "Not yet available", which answers
    // nothing. Say what is actually missing.
    unavailableReason: 'The Jira connection form is still being built.',
  },
];

export function getProvider(id: string): ProviderDescriptor | undefined {
  return PROVIDERS.find((p) => p.id === id);
}
